import { action, makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import MemberStore from 'iam/MemberStore'
import WorkspaceStore from 'iam/WorkspaceStore'
import WorkspaceMembershipStore, {
  Membership,
} from 'iam/WorkspaceMembershipStore'
import { IAMAPI } from 'iam/API'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { FreshbooksCallCenterAPI } from 'freshbooks/API'
import { AlertFactory } from 'alerts/Alert'
import { CallCenterCustomerLookupViewModel } from './CallCenterCustomerLookupViewModel'
import { CustomerStore } from 'iam/CustomerStore'
import { filterCallCenterMemberships } from 'call-center/utils'
import JobCreationRootViewModel from 'jobs/creation/JobCreationRootViewModel'
import JobStore from 'jobs/JobStore'
import { JobLegendServiceType } from 'jobs/Job'
import { Id, Router } from 'types/definitions'
import CallCenterJobViewModel from './views/JobSection/CallCenterJobViewModel'
import { CallCenterAddClientDialogViewModel } from './views/AddClientDialog/CallCenterAddClientDialogViewModel'
import SummaryStore from '../jobs/SummaryStore'
import CouponStore from '../billing/CouponStore'
import link from '../infra/link'
import BillingStore from '../billing/BillingStore'

export enum CallCenterSectionId {
  CustomerLookup = 'CustomerLookup',
  Job = 'Job',
  Checkout = 'Checkout',
}

export interface CallCenterSection {
  id: CallCenterSectionId
  title: string
}

export class CallCenterViewModel {
  private jobStore: JobStore
  private router: Router

  customerLookupVM: CallCenterCustomerLookupViewModel

  addClientDialogVM: CallCenterAddClientDialogViewModel

  jobVM: CallCenterJobViewModel

  @observable
  activeSection: CallCenterSectionId = CallCenterSectionId.CustomerLookup

  getBreadcrumbItems(isOnCheckoutPage: boolean): CallCenterSection[] {
    const items: CallCenterSection[] = [
      { id: CallCenterSectionId.CustomerLookup, title: 'Customer lookup' },
    ]

    if (this.jobVM.jobCreation !== null) {
      items.push({ id: CallCenterSectionId.Job, title: 'Tax situation' })
    }

    if (isOnCheckoutPage) {
      items.push({ id: CallCenterSectionId.Checkout, title: 'Checkout' })
    }

    return items
  }

  @observable
  jobId: string | null = null

  constructor(
    freshbooksCallCenterAPI: FreshbooksCallCenterAPI,
    workspaceMembershipStore: WorkspaceMembershipStore,
    workspaceStore: WorkspaceStore,
    iamAPI: IAMAPI,
    memberStore: MemberStore,
    customerStore: CustomerStore,
    jobSearchStore: JobSearchStore,
    jobStore: JobStore,
    publishedLegendStore: PublishedLegendStore,
    jobCreationRoot: JobCreationRootViewModel,
    summaryStore: SummaryStore,
    couponStore: CouponStore,
    billingStore: BillingStore,
    newAlert: AlertFactory,
    router: Router,
  ) {
    makeObservable(this)
    this.jobStore = jobStore
    this.router = router
    this.customerLookupVM = new CallCenterCustomerLookupViewModel(
      freshbooksCallCenterAPI,
      workspaceMembershipStore,
      workspaceStore,
      iamAPI,
      memberStore,
      jobSearchStore,
      publishedLegendStore,
      newAlert,
    )
    this.jobVM = new CallCenterJobViewModel(
      jobCreationRoot,
      summaryStore,
      couponStore,
      publishedLegendStore,
      jobStore,
      billingStore,
      newAlert,
      router,
    )
    this.addClientDialogVM = new CallCenterAddClientDialogViewModel(
      memberStore,
      customerStore,
      () =>
        filterCallCenterMemberships(
          workspaceMembershipStore.getCurrentUserWorkspaceMemberships(),
        ),
      this.handleClientCreated,
      newAlert,
    )
  }

  @action.bound
  async loadJobFromUrl(jobId?: string) {
    if (!jobId) {
      return
    }

    runInAction(() => {
      this.jobId = jobId
    })

    await this.loadJob(jobId)
  }

  @action.bound
  setActiveSection(section: CallCenterSectionId) {
    this.activeSection = section

    switch (section) {
      case CallCenterSectionId.CustomerLookup: {
        this.jobVM.resetActiveJobState()
        this.jobId = null

        this.router.push('/call-center')
        break
      }
      case CallCenterSectionId.Job: {
        if (this.jobId) {
          this.router.push(link.callCenterJob(this.jobId))
        }
        break
      }
      case CallCenterSectionId.Checkout: {
        this.router.push(link.callCenterJobCheckout(this.jobId!))
      }
    }
  }

  @action.bound
  requestCreateJob(membership: Membership) {
    this.jobVM.confirmationDialog.open(membership)
  }

  @action.bound
  private handleClientCreated(membership: Membership) {
    this.jobVM.confirmationDialog.open(membership)
  }

  activate() {
    this.customerLookupVM.activate()
    this.jobVM.activate()
  }

  deactivate() {
    this.customerLookupVM.deactivate()
    this.jobVM.deactivate()
  }

  loadJob = task.resolved(async (jobId: string) => {
    const job = await this.jobStore.fetchJob(Id.assert(jobId))

    if (!job.isDraft) {
      throw new Error('Job is not supported')
    }

    if (job.legendServiceType !== JobLegendServiceType.TAX) {
      throw new Error('Job is not supported')
    }

    // Load the job into tax situation view model.
    await this.jobVM.loadFromExistingJob(job)

    return job
  })
}
